import { Flex, GapSize } from "@/components/atoms/flex";
import {
	ContactType,
	type InternalPresenceDto,
	type PresenceDto,
} from "@/repository/models";
import { Avatar, Divider, Text } from "@fluentui/react-components";
import {
	Call20Regular,
	Location20Regular,
	Mail20Regular,
	Person20Regular,
	Phone20Regular,
} from "@fluentui/react-icons";
import type { ProfilePictureProps } from "./types";
import { useQuery } from "@tanstack/react-query";
import { UserTeamsPresenceKey, getTeamsPresence } from "@/repository/queryFns/teams";
import {
	UserInternalPresenceKey,
	getUserInternalPresence,
} from "@/repository/queryFns";
import { useUserPhoto } from "@/hooks/userPhoto";
import { useStyle } from "./style";
import { useMemo, useState } from "react";
import { mapPresenceToStatus } from "@/common/mappers/presenceMapper";
import { translateActivity } from "@/common/helpers/presenceHelper";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";
import { useIntl } from "react-intl";

export const ProfilePicture = (props: ProfilePictureProps) => {
	const { id, name, contactType, hasCC4TeamsClient, details } = props;
	const classes = useStyle();
	const intl = useIntl();

	const photoUrl = useUserPhoto(id, contactType === ContactType.AzureActiveDirectory);

	const presenceResult = useQuery<any>({
		retry: false,
		queryKey: [UserTeamsPresenceKey, id],
		queryFn: () => getTeamsPresence(id),
		gcTime: 0,
	});

	const badgeStatus = useMemo<any>(() => {
		if (!!presenceResult.data) {
			return {
				status: mapPresenceToStatus(presenceResult.data.availability),
				outOfOffice: false,
			};
		}
		return undefined;
	}, [presenceResult.data]);

	const internalPresenceResult = useQuery<PresenceDto>({
		retry: false,
		enabled: !!hasCC4TeamsClient,
		queryKey: [UserInternalPresenceKey, id],
		queryFn: () => getUserInternalPresence(id),
		// Keep the entry past unmount (the card only exists while the popover is open) so a re-hover
		// renders the last known status instead of an empty row, but leave it stale so that re-hover also
		// refetches: the InternalPresenceChanged subscription below dies with the card, so nothing keeps a
		// cached value current between hovers and a non-zero staleTime would serve a status that has since
		// changed — an "Available" colleague who is by then on a call.
		gcTime: 5 * 60_000,
	});

	// Live status while the card stays open. The event is broadcast tenant-wide, so this only filters for
	// the hovered contact — no extra requests. Gated on hasCC4TeamsClient like the query above, otherwise a
	// push would render an internal status (including a free-text reason) for a contact the card must not
	// show one for.
	const [livePresence, setLivePresence] = useState<PresenceDto | undefined>();

	notificationListener<InternalPresenceDto>(
		ServerEvent.InternalPresenceChanged,
		(internalPresenceDto: InternalPresenceDto) => {
			if (
				!!hasCC4TeamsClient &&
				internalPresenceDto.presence?.id?.toLowerCase() === id.toLowerCase()
			) {
				setLivePresence(internalPresenceDto.presence);
			}
		}
	);

	const internalStatusText = useMemo<string | undefined>(() => {
		const presence = hasCC4TeamsClient
			? (livePresence ?? internalPresenceResult.data)
			: undefined;
		const status = presence?.activity || presence?.availability;

		return status ? translateActivity(status, intl) : undefined;
	}, [livePresence, hasCC4TeamsClient, internalPresenceResult.data, intl]);

	const detailRows = useMemo(() => {
		const location = [details?.office, details?.city, details?.country]
			.filter((part) => !!part)
			.join(", ");
		const isUpnDifferentFromEmail =
			(details?.userPrincipalName ?? "").toLowerCase() !==
			(details?.email ?? "").toLowerCase();

		return [
			{
				key: "email",
				labelId: "Contacts.Form.Email",
				value: details?.email,
				icon: <Mail20Regular />,
			},
			{
				key: "upn",
				labelId: "Contacts.Form.UserPrincipalName",
				value: isUpnDifferentFromEmail ? details?.userPrincipalName : undefined,
				icon: <Person20Regular />,
			},
			{
				key: "mobilePhone",
				labelId: "Contacts.Form.MobilePhoneNumber",
				value: details?.mobilePhone,
				icon: <Phone20Regular />,
			},
			{
				key: "businessPhone",
				labelId: "Contacts.Form.BusinessPhoneNumber",
				value: details?.businessPhone,
				icon: <Call20Regular />,
			},
			{
				key: "location",
				labelId: "Contacts.Form.Location",
				value: location,
				icon: <Location20Regular />,
			},
		].filter((row) => !!row.value);
	}, [details]);

	return (
		<Flex column className={classes.root} gap={GapSize.Medium}>
			<Flex vAlign="center" gap={GapSize.Medium}>
				<Avatar
					size={72}
					image={photoUrl ? { src: photoUrl } : undefined}
					badge={badgeStatus}
					name={name}
					className={hasCC4TeamsClient ? classes.hasClientBorder : ""}
				/>

				<Flex column className={classes.header}>
					<Text
						weight="semibold"
						size={400}
						className={classes.headerName}
						children={name}
					/>
					{!!details?.jobTitle && (
						<Text className={classes.headerSecondary} children={details.jobTitle} />
					)}
					{!!details?.department && (
						<Text className={classes.headerSecondary} children={details.department} />
					)}
					{!!details?.companyName && (
						<Text
							className={classes.headerSecondary}
							children={details.companyName}
						/>
					)}
					{hasCC4TeamsClient && (
						<Text
							className={classes.headerSecondary}
							children={intl.formatMessage({
								id: "Contacts.HasCC4TeamsClient",
							})}
						/>
					)}
					{!!internalStatusText && (
						<Text
							className={classes.headerSecondary}
							children={internalStatusText}
						/>
					)}
				</Flex>
			</Flex>

			{detailRows.length > 0 && (
				<>
					<Divider />
					<div className={classes.detailsList}>
						{detailRows.map((row) => {
							// The icon carries the field name only as a tooltip for mouse users; assistive
							// technology gets it as visually hidden text in front of the value, because the
							// icon is decorative and a plain span cannot be named with aria-label.
							const label = intl.formatMessage({ id: row.labelId });

							return (
								<Flex key={row.key} vAlign="center" gap={GapSize.Small}>
									<span className={classes.detailIcon} title={label} aria-hidden="true">
										{row.icon}
									</span>
									<Text className={classes.detailValue}>
										<span className={classes.visuallyHidden}>{`${label}: `}</span>
										{row.value}
									</Text>
								</Flex>
							);
						})}
					</div>
				</>
			)}
		</Flex>
	);
};
